import React, { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { OrganizationDetailsQuery } from '@plug/graphql/queries/organization-details';
import { ORGANIZATION_DETAILS } from '@plug/domains/organization/graphql/queries/organization-details';
import { USER_LOCAL_STORAGE_KEY, WHITE_LABEL_TITLES } from '@plug/config/consts';

import cookie from 'js-cookie';

const GLEAP_SDK_KEY = 'XcCEJgBTiJKFqOM4yTYBeJqc8741koTx';
const GLEAP_SCRIPT_ID = 'gleap-sdk-script';
const GLEAP_SCRIPT_URL = 'https://js.gleap.io/latest/index.js';

type GleapIdentifyData = {
  email?: string;
  name?: string;
  company?: {
    id?: string;
    name?: string;
  };
  customData?: {
    domain?: string;
    hostname?: string;
    integration?: string;
    role?: string;
    whitelabel?: string;
  };
};

const getWhitelabelName = (hostname: string): string => {
  return WHITE_LABEL_TITLES[hostname as keyof typeof WHITE_LABEL_TITLES] ?? hostname;
};

type GleapSDK = {
  initialize: (sdkKey: string) => void;
  identify: (userId: string, customerData: GleapIdentifyData) => void;
  showFeedbackButton: (show: boolean) => void;
  destroy: () => void;
};

declare global {
  interface Window {
    Gleap?: GleapSDK;
  }
}

let gleapScriptPromise: Promise<GleapSDK> | null = null;

const loadGleapScript = (): Promise<GleapSDK> => {
  if (window.Gleap) {
    return Promise.resolve(window.Gleap);
  }

  if (gleapScriptPromise) {
    return gleapScriptPromise;
  }

  gleapScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GLEAP_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.Gleap) {
          resolve(window.Gleap);
          return;
        }

        reject(new Error('Gleap SDK loaded but window.Gleap is unavailable.'));
      });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Gleap SDK.')));
      return;
    }

    const script = document.createElement('script');
    script.id = GLEAP_SCRIPT_ID;
    script.src = GLEAP_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Gleap) {
        resolve(window.Gleap);
        return;
      }

      reject(new Error('Gleap SDK loaded but window.Gleap is unavailable.'));
    };
    script.onerror = () => reject(new Error('Failed to load Gleap SDK.'));
    document.body.appendChild(script);
  });

  return gleapScriptPromise;
};

export const GleapComponent = () => {
  const { data: organizationDetailsData } = useQuery<OrganizationDetailsQuery>(
    ORGANIZATION_DETAILS,
  );
  const gleapInitializedRef = useRef<boolean>(false);
  const user = JSON.parse(decodeURIComponent(cookie.get(USER_LOCAL_STORAGE_KEY) || '{}'));
  const organization = organizationDetailsData?.organizationDetails;
  const organizationId = organization?.id;
  const organizationName = organization?.name;
  const organizationDomain = organization?.domain;
  const integrationType = organization?.integration?.type;
  const role = organization?.organizationRole[0]?.name;
  const hostname = window.location.hostname;
  const userId = user?.id;
  const userEmail = user?.email;
  const userName = user?.username;

  useEffect(() => {
    if (!organizationId || !userId) return;

    let isMounted = true;

    const syncGleap = async () => {
      try {
        const Gleap = await loadGleapScript();
        if (!isMounted) return;

        if (!gleapInitializedRef.current) {
          Gleap.initialize(GLEAP_SDK_KEY);
          gleapInitializedRef.current = true;
        }

        Gleap.identify(userId, {
          email: userEmail,
          name: userName,
          company: {
            id: organizationId,
            name: organizationName,
          },
          customData: {
            domain: organizationDomain,
            hostname,
            integration: integrationType,
            role,
            whitelabel: getWhitelabelName(hostname),
          },
        });
        Gleap.showFeedbackButton(role === 'ADMIN');
      } catch (error) {
        console.error('Failed to initialize Gleap SDK.', error);
      }
    };

    syncGleap();

    return () => {
      isMounted = false;
    };
  }, [
    hostname,
    integrationType,
    organizationDomain,
    organizationId,
    organizationName,
    role,
    userEmail,
    userId,
    userName,
  ]);

  return <></>;
};
